import React, { useState, useEffect } from 'react';

/**
 * LearningCircle - Minimal, human connection
 *
 * Transforms "I am learning" into "We are learning"
 *
 * Design principles:
 * - Max 8-12 people per circle
 * - Share: current level, optional teach-back excerpts
 * - Don't share: scores, streaks, rankings
 * - Purpose: "We're learning together"
 * - Ubuntu enters the system
 */
function LearningCircle({ currentLevel, onClose, onBackToSIFISO }) {
  const [circles, setCircles] = useState([]);
  const [activeCircle, setActiveCircle] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCircleName, setNewCircleName] = useState('');
  const [yourName, setYourName] = useState('');

  // Load circles from localStorage
  useEffect(() => {
    const storedCircles = localStorage.getItem('sifiso-circles');
    if (storedCircles) {
      setCircles(JSON.parse(storedCircles));
    }

    const storedName = localStorage.getItem('sifiso-learner-name');
    if (storedName) {
      setYourName(storedName);
    }

    const storedActiveCircle = localStorage.getItem('sifiso-active-circle');
    if (storedActiveCircle) {
      setActiveCircle(storedActiveCircle);
    }
  }, []);

  // Save circles to localStorage
  const saveCircles = (updatedCircles) => {
    localStorage.setItem('sifiso-circles', JSON.stringify(updatedCircles));
    setCircles(updatedCircles);
  };

  const createCircle = () => {
    if (!newCircleName.trim() || !yourName.trim()) return;

    const circleId = `circle-${Date.now()}`;
    const newCircle = {
      id: circleId,
      name: newCircleName.trim(),
      maxMembers: 12,
      members: [
        {
          id: `member-${Date.now()}`,
          name: yourName.trim(),
          currentLevel: currentLevel + 1,
          joinedAt: new Date().toISOString()
        }
      ]
    };

    const updatedCircles = [...circles, newCircle];
    saveCircles(updatedCircles);
    setActiveCircle(circleId);
    localStorage.setItem('sifiso-active-circle', circleId);
    localStorage.setItem('sifiso-learner-name', yourName.trim());
    setShowCreateForm(false);
    setNewCircleName('');
  };

  const joinCircle = (circleId) => {
    if (!yourName.trim()) {
      alert('Please enter your name first');
      return;
    }

    const circle = circles.find(c => c.id === circleId);
    if (!circle) return;

    if (circle.members.length >= circle.maxMembers) {
      alert('This circle is full (max 12 members)');
      return;
    }

    const alreadyMember = circle.members.some(m => m.name === yourName.trim());
    if (alreadyMember) {
      alert('You are already a member of this circle');
      return;
    }

    const updatedCircles = circles.map(c => {
      if (c.id === circleId) {
        return {
          ...c,
          members: [
            ...c.members,
            {
              id: `member-${Date.now()}`,
              name: yourName.trim(),
              currentLevel: currentLevel + 1,
              joinedAt: new Date().toISOString()
            }
          ]
        };
      }
      return c;
    });

    saveCircles(updatedCircles);
    setActiveCircle(circleId);
    localStorage.setItem('sifiso-active-circle', circleId);
    localStorage.setItem('sifiso-learner-name', yourName.trim());
  };

  const leaveCircle = (circleId) => {
    const updatedCircles = circles.map(c => {
      if (c.id === circleId) {
        return {
          ...c,
          members: c.members.filter(m => m.name !== yourName)
        };
      }
      return c;
    }).filter(c => c.members.length > 0); // Remove empty circles

    saveCircles(updatedCircles);
    if (activeCircle === circleId) {
      setActiveCircle(null);
      localStorage.removeItem('sifiso-active-circle');
    }
  };

  const updateProgress = () => {
    if (!activeCircle || !yourName) return;

    const updatedCircles = circles.map(c => {
      if (c.id === activeCircle) {
        return {
          ...c,
          members: c.members.map(m => {
            if (m.name === yourName) {
              return { ...m, currentLevel: currentLevel + 1 };
            }
            return m;
          })
        };
      }
      return c;
    });

    saveCircles(updatedCircles);
  };

  // Update progress when level changes
  useEffect(() => {
    updateProgress();
  }, [currentLevel]);

  const myCircle = circles.find(c => c.id === activeCircle);

  return (
    <div className="min-h-screen text-white" style={{ background: '#1a202c' }}>
      <div className="max-w-4xl mx-auto p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Learning Circles</h1>
            <p style={{ color: '#CCCCCC' }}>We're learning together — Ubuntu</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg transition text-white text-sm"
              style={{ background: '#008080' }}
            >
              ← Back to Levels
            </button>
            <button
              onClick={onBackToSIFISO}
              className="px-4 py-2 rounded-lg transition text-white text-sm"
              style={{ background: '#475569' }}
            >
              🏠 SIFISO
            </button>
          </div>
        </div>

        {/* Your name input */}
        {!yourName && (
          <div className="mb-6 p-6 bg-slate-800 rounded-lg border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-3">First, what's your name?</h3>
            <input
              type="text"
              value={yourName}
              onChange={(e) => setYourName(e.target.value)}
              placeholder="Your name"
              className="w-full p-3 bg-slate-900 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500"
            />
            <p className="text-slate-500 text-sm mt-2">
              This is how circle members will see you
            </p>
          </div>
        )}

        {/* My Circle */}
        {myCircle && (
          <div className="mb-6 p-6 bg-slate-800 rounded-lg border border-green-600/30">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-white mb-1">{myCircle.name}</h2>
                <p className="text-slate-400 text-sm">
                  {myCircle.members.length} / {myCircle.maxMembers} members
                </p>
              </div>
              <button
                onClick={() => leaveCircle(myCircle.id)}
                className="px-3 py-1 text-red-400 hover:text-red-300 text-sm transition"
              >
                Leave Circle
              </button>
            </div>

            {/* Members */}
            <div className="space-y-3">
              {myCircle.members.map(member => (
                <div
                  key={member.id}
                  className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-900/30 border border-blue-500/30 flex items-center justify-center">
                      <span className="text-lg">{member.name.charAt(0).toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-200">{member.name}</p>
                      <p className="text-slate-400 text-sm">
                        Currently on Level {member.currentLevel}
                      </p>
                    </div>
                  </div>
                  {member.name === yourName && (
                    <span className="text-blue-400 text-sm">You</span>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-4 p-4 bg-slate-900/30 rounded-lg border border-slate-700/30">
              <p className="text-slate-400 text-sm leading-relaxed">
                Your progress updates automatically as you complete levels.
                Circle members can see which level you're on, but not your scores or streaks.
              </p>
            </div>
          </div>
        )}

        {/* Available Circles */}
        {!myCircle && circles.length > 0 && (
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-white mb-4">Join a Circle</h3>
            <div className="space-y-3">
              {circles.map(circle => (
                <div
                  key={circle.id}
                  className="p-4 bg-slate-800 rounded-lg border border-slate-700 flex items-center justify-between"
                >
                  <div>
                    <h4 className="font-semibold text-slate-200 mb-1">{circle.name}</h4>
                    <p className="text-slate-400 text-sm">
                      {circle.members.length} / {circle.maxMembers} members
                    </p>
                  </div>
                  <button
                    onClick={() => joinCircle(circle.id)}
                    disabled={circle.members.length >= circle.maxMembers}
                    className={`px-4 py-2 rounded-lg font-semibold transition ${
                      circle.members.length >= circle.maxMembers
                        ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {circle.members.length >= circle.maxMembers ? 'Full' : 'Join'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Create Circle */}
        {!myCircle && (
          <div className="p-6 bg-slate-800 rounded-lg border border-slate-700">
            {!showCreateForm ? (
              <div className="text-center">
                <p className="text-slate-400 mb-4">
                  {circles.length === 0
                    ? "No circles yet. Start one."
                    : "Don't see a circle you want to join?"
                  }
                </p>
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold transition"
                  disabled={!yourName}
                >
                  Create a Circle
                </button>
                {!yourName && (
                  <p className="text-slate-500 text-sm mt-2">Enter your name first</p>
                )}
              </div>
            ) : (
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Create Your Circle</h3>
                <input
                  type="text"
                  value={newCircleName}
                  onChange={(e) => setNewCircleName(e.target.value)}
                  placeholder="Circle name (e.g., 'Township Learners', 'Weekend Study Group')"
                  className="w-full p-3 bg-slate-900 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500 mb-4"
                />
                <p className="text-slate-400 text-sm mb-4">
                  Max 12 members. You'll be the first member.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowCreateForm(false)}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={createCircle}
                    disabled={!newCircleName.trim()}
                    className={`px-6 py-3 rounded-lg font-semibold transition ${
                      !newCircleName.trim()
                        ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    Create Circle
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Ubuntu note */}
        <div className="mt-6 p-4 bg-slate-900/30 rounded-lg border border-slate-700/30">
          <p className="text-slate-400 text-sm leading-relaxed">
            <span className="font-semibold text-slate-300">Ubuntu:</span> I am because we are.
            Learning circles are small groups learning together. No competition. No rankings.
            Just shared progress.
          </p>
        </div>
      </div>
    </div>
  );
}

export default LearningCircle;
